import React, { useState, useEffect } from 'react';
import { JournalistCard } from './JournalistCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Search, Filter, Globe } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { authFetch } from '@/utils/auth-fetch';

interface Journalist {
  id: string;
  site_id?: string;
  sector_id?: number;
  sector_ids?: number[];
  sector_name?: string;
  sector_names?: string[];
  is_generic?: boolean;
  is_organization_exclusive?: boolean;
  organization_name?: string;
  language?: string;
  name: string;
  title: string;
  bio?: string;
  photo_url?: string;
  style: 'professional' | 'sporty' | 'business' | 'conversational';
  voice_gender: 'male' | 'female' | 'neutral';
  reference_articles?: any[];
  sample_questions?: string[];
  is_active: boolean;
  total_interviews: number;
  average_rating?: number;
  created_at: string;
}

interface JournalistGalleryProps {
  siteId?: string;          // Site ID for filtering (shows system + site-exclusive)
  sectorId?: number;
  sectorIds?: number[];     // Array of sector IDs for filtering
  onSelect?: (journalist: Journalist) => void;
  onEdit?: (journalist: Journalist) => void;
  showEditButton?: boolean;
  selectable?: boolean;
  showFilters?: boolean;
  selectedJournalistId?: string;
  defaultShowAll?: boolean;  // Start with "show all" enabled
}

export function JournalistGallery({
  siteId,
  sectorId,
  sectorIds,
  onSelect,
  onEdit,
  showEditButton = false,
  selectable = false,
  showFilters = true,
  selectedJournalistId,
  defaultShowAll = false
}: JournalistGalleryProps) {
  const [journalists, setJournalists] = useState<Journalist[]>([]);
  const [filteredJournalists, setFilteredJournalists] = useState<Journalist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [styleFilter, setStyleFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [showAll, setShowAll] = useState(defaultShowAll);

  // Combine sectorId and sectorIds for API call
  const effectiveSectorIds = sectorIds || (sectorId ? [sectorId] : []);

  useEffect(() => {
    fetchJournalists();
  }, [siteId, sectorId, sectorIds, showAll]);

  useEffect(() => {
    applyFilters();
  }, [journalists, searchQuery, styleFilter, activeFilter, languageFilter]);

  const fetchJournalists = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();

      // If showAll is true, fetch all journalists
      if (showAll) {
        params.append('showAll', 'true');
      } else {
        // Filter by site (shows system + site-exclusive journalists)
        if (siteId) {
          params.append('siteId', siteId);
        }
        // Filter by sectors (includes generic journalists by default)
        if (effectiveSectorIds.length > 0) {
          params.append('sectorIds', effectiveSectorIds.join(','));
          params.append('includeGeneric', 'true');
        }
      }

      const response = await authFetch(`/api/journalists?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch journalists');
      }

      const data = await response.json();
      setJournalists(data.journalists || []);
    } catch (err) {
      console.error('Error fetching journalists:', err);
      setError(err instanceof Error ? err.message : 'Failed to load journalists');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...journalists];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (j) =>
          j.name.toLowerCase().includes(query) ||
          j.title?.toLowerCase().includes(query) ||
          j.bio?.toLowerCase().includes(query)
      );
    }

    // Style filter
    if (styleFilter !== 'all') {
      filtered = filtered.filter((j) => j.style === styleFilter);
    }

    // Active filter
    if (activeFilter === 'active') {
      filtered = filtered.filter((j) => j.is_active);
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter((j) => !j.is_active);
    }

    // Language filter
    if (languageFilter !== 'all') {
      filtered = filtered.filter((j) => j.language === languageFilter);
    }

    setFilteredJournalists(filtered);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      {showFilters && (
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4" />
            <h3 className="font-semibold">Filters</h3>
          </div>

          {/* Show All toggle - only show when sector filtering is available */}
          {effectiveSectorIds.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {showAll ? 'Showing all journalists' : 'Showing generic + sector-specific journalists'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="show-all" className="text-sm text-muted-foreground">Show All</Label>
                <Switch
                  id="show-all"
                  checked={showAll}
                  onCheckedChange={setShowAll}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search journalists..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Language filter */}
            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select value={languageFilter} onValueChange={setLanguageFilter}>
                <SelectTrigger id="language">
                  <SelectValue placeholder="All languages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="de">German</SelectItem>
                  <SelectItem value="it">Italian</SelectItem>
                  <SelectItem value="pt">Portuguese</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Style filter */}
            <div className="space-y-2">
              <Label htmlFor="style">Style</Label>
              <Select value={styleFilter} onValueChange={setStyleFilter}>
                <SelectTrigger id="style">
                  <SelectValue placeholder="All styles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Styles</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="sporty">Sporty</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="conversational">Conversational</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active filter */}
            <div className="space-y-2">
              <Label htmlFor="active">Status</Label>
              <Select value={activeFilter} onValueChange={setActiveFilter}>
                <SelectTrigger id="active">
                  <SelectValue placeholder="All journalists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Journalists</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Clear filters */}
          {(searchQuery || styleFilter !== 'all' || activeFilter !== 'all' || languageFilter !== 'all') && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setStyleFilter('all');
                  setActiveFilter('all');
                  setLanguageFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredJournalists.length} of {journalists.length} journalists
      </div>

      {/* Gallery grid */}
      {filteredJournalists.length === 0 ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {journalists.length === 0
              ? 'No journalists found. Create your first journalist to get started.'
              : 'No journalists match your filters. Try adjusting your search criteria.'}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredJournalists.map((journalist) => (
            <JournalistCard
              key={journalist.id}
              journalist={journalist}
              onEdit={onEdit}
              onSelect={onSelect}
              showEditButton={showEditButton}
              selectable={selectable}
              isSelected={journalist.id === selectedJournalistId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
